import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { OL_COLOUR_MELON } from 'src/web/components/Style/colours';
import {
  AssignToCourseTaskResult,
  BackgroundTaskEnum,
  BackgroundTaskStatus,
} from 'src/resource/BackgroundTaskResource/types';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import { confirmDialog } from 'src/web/components/Modals/ConfirmDialog';
import { Header } from '../../styles';
import {
  useGetBackgroundTasksApi,
  useDeleteBackgroundTaskApi,
} from '../../hooks';
import {
  BackgroundTaskWrapper,
  ErrorInfoWrapper,
  StyledButton,
  StyledIconButton,
  IconWrapper,
} from './styles';
import { MemberType } from '../../types';

export const RecentlyAssignedCourses: React.FC<{
  currentInstitutionPath: string;
  memberType: MemberType;
}> = ({ currentInstitutionPath, memberType }) => {
  const {
    getBackgroundTasks,
    isLoading: isLoadingBackgroundTask,
  } = useGetBackgroundTasksApi(currentInstitutionPath);
  const { deleteBackgroundTask } = useDeleteBackgroundTaskApi();
  const [assignMemberToCourseTasks, setAssignMemberToCourseTasks] = useState<
    AssignToCourseTaskResult[]
  >([]);
  const [showError, setShowError] = useState<{ [taskId: string]: boolean }>({});

  const getAssignToCourseTasks = async (): Promise<void> => {
    const assignActiveMemberToCourseRes = await getBackgroundTasks({
      taskType:
        memberType === 'team members'
          ? BackgroundTaskEnum.ASSIGN_TEAM_MEMBER_TO_COURSE
          : BackgroundTaskEnum.ASSIGN_LEARNER_TO_COURSE,
    });
    const assignPendingMemberToCourseRes = await getBackgroundTasks({
      taskType:
        memberType === 'team members'
          ? BackgroundTaskEnum.ASSIGN_PENDING_TEAM_MEMBER_TO_COURSE
          : BackgroundTaskEnum.ASSIGN_PENDING_LEARNER_TO_COURSE,
    });
    setAssignMemberToCourseTasks([
      ...assignActiveMemberToCourseRes,
      ...assignPendingMemberToCourseRes,
    ] as AssignToCourseTaskResult[]);
  };

  useEffect(() => {
    getAssignToCourseTasks();
  }, [currentInstitutionPath, memberType]);

  return (
    <>
      <Header>Recently assigned courses</Header>
      {isLoadingBackgroundTask && <Spinner />}
      {assignMemberToCourseTasks.length === 0 && !isLoadingBackgroundTask && (
        <BackgroundTaskWrapper>
          There are no recently assigned courses
        </BackgroundTaskWrapper>
      )}
      {assignMemberToCourseTasks.map(({ id, status, result, params }) => (
        <>
          <BackgroundTaskWrapper>
            <IconWrapper>
              {status === BackgroundTaskStatus.COMPLETED && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  color={OL_COLOUR_MELON}
                  size="lg"
                />
              )}
              {(status === BackgroundTaskStatus.IN_PROGRESS ||
                status === BackgroundTaskStatus.NOT_STARTED) && <Spinner />}
            </IconWrapper>
            <strong>
              {params.total_assigned_users - (result?.totalFailed || 0)}{' '}
              {memberType}
            </strong>{' '}
            {status === BackgroundTaskStatus.COMPLETED
              ? 'have been'
              : 'are being'}{' '}
            assigned to <strong>{params.course_roles.length} courses</strong>
            <StyledIconButton
              hiddenLabel="Remove this task"
              icon="remove"
              onClick={() => {
                confirmDialog({
                  title: 'Remove this task?',
                  message: 'Are you sure?',
                  cancelLabel: 'Cancel',
                  okHandler: () => {
                    deleteBackgroundTask(id).then(getAssignToCourseTasks);
                  },
                });
              }}
            />
            {!!result?.totalFailed && (
              <StyledButton
                label={`View ${result.totalFailed} errors`}
                icon="chevron-right"
                colour="danger"
                onClick={() => {
                  setShowError({ ...showError, [id]: !showError[id] });
                }}
              />
            )}
          </BackgroundTaskWrapper>
          {showError[id] && result && (
            <ErrorInfoWrapper>
              {Object.keys(result.failedUsers) &&
                Object.entries(result.failedUsers).map(([userId, reasons]) => (
                  <>
                    {reasons.map((reason) => (
                      <div>
                        <strong>{userId}</strong> could not be assigned to{' '}
                        <strong>{reason.path}</strong> – {reason.error}
                      </div>
                    ))}
                  </>
                ))}
            </ErrorInfoWrapper>
          )}
        </>
      ))}
    </>
  );
};
